/**
 * useSheetDismiss — touch-driven pull-to-dismiss for slide-up sheets.
 *
 * Tracks touchstart/move/end on a sheet header, translates it along Y,
 * and calls onClose when the drag passes a velocity/distance threshold.
 * Pure DOM/touch — no animation library.
 *
 * Consumers: App.tsx detail sheet, collection sheet and other bottom
 * sheets wire the returned handlers onto the drag handle.
 */
import { useState, useCallback, useRef } from 'react';

function useSheetDismiss(onClose: () => void) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);
  const currentY = useRef(0);
  const isDragging = useRef(false);
  const [dragOffset, setDragOffset] = useState(0);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const sheet = sheetRef.current;
    if (!sheet) return;
    // Only allow pull-to-dismiss when scrolled to top
    if (sheet.scrollTop > 5) return;
    startY.current = e.touches[0].clientY;
    currentY.current = startY.current;
    isDragging.current = true;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current) return;
    currentY.current = e.touches[0].clientY;
    const delta = currentY.current - startY.current;
    if (delta < 0) { // Scrolling up — let normal scroll handle it
      isDragging.current = false;
      setDragOffset(0);
      return;
    }
    // Rubber-band effect: diminishing returns past 100px
    const dampened = delta < 100 ? delta : 100 + (delta - 100) * 0.3;
    setDragOffset(dampened);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging.current && dragOffset === 0) return;
    isDragging.current = false;
    if (dragOffset > 120) {
      // Dismiss threshold reached
      setDragOffset(0);
      onClose();
    } else {
      // Snap back
      setDragOffset(0);
    }
  }, [dragOffset, onClose]);

  const sheetStyle: React.CSSProperties = dragOffset > 0
    ? { transform: `translateY(${dragOffset}px)`, transition: isDragging.current ? "none" : "transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)", overflowY: "hidden" }
    : {};

  const backdropOpacity = dragOffset > 0 ? Math.max(0, 1 - dragOffset / 300) : 1;

  return { sheetRef, sheetStyle, backdropOpacity, handleTouchStart, handleTouchMove, handleTouchEnd };
}

export { useSheetDismiss };
